import React from 'react';
import { useT } from '../lib/i18n';
import { cn } from '../lib/cn';

/* ─── Inline SVG icons (no deps) ─── */
const IconArrowRight = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);

const IconCheck = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ─── Tool icons for features grid ─── */
const toolIcons: Record<string, React.FC<{ className?: string }>> = {
  brain: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a5 5 0 0 1 4.47 7.23A4 4 0 0 1 17 17h-1v3a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-3H7a4 4 0 0 1 .53-7.97A5 5 0 0 1 12 2z" />
    </svg>
  ),
  compose: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  coach: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  reply: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  analytics: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" />
    </svg>
  ),
  algo: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  researcher: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  inspiration: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  education: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  ),
};



/* ─── Stagger animation CSS (injected once) ─── */
const staggerCSS = `
@keyframes lp-rise {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes lp-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes lp-scale {
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
}
.lp-rise { animation: lp-rise 0.7s cubic-bezier(0.16,1,0.3,1) both; }
.lp-fade { animation: lp-fade 0.6s ease-out both; }
.lp-scale { animation: lp-scale 0.6s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes lp-grid-dot {
  0%, 100% { opacity: 0.03; }
  50% { opacity: 0.08; }
}
`;

/* ─── Features / Pricing keys (i18n resolved inside component) ─── */
const toolKeys = [
  { key: 'brain', color: 'brand' },
  { key: 'compose', color: 'accent' },
  { key: 'coach', color: 'brand' },
  { key: 'reply', color: 'accent' },
  { key: 'analytics', color: 'brand' },
  { key: 'algo', color: 'accent' },
  { key: 'researcher', color: 'brand' },
  { key: 'inspiration', color: 'accent' },
  { key: 'education', color: 'brand' },
];

/* ─── Main Component ─── */
export const TwitterLogin: React.FC = () => {
  const { t, locale, setLocale } = useT();

  const handleGetStarted = () => {
    localStorage.setItem('user', JSON.stringify({ id: 'archive_user', username: 'user' }));
    window.location.href = '/brain-dump';
  };

  return (
    <div className="min-h-screen bg-bg text-text-primary overflow-x-hidden">
      <style>{staggerCSS}</style>

      {/* ── Dot grid background ── */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* ── Nav ── */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-bg/80 border-b border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo192.png" alt="Bangerfy" className="w-7 h-7 rounded-[8px]" />
            <span className="text-[15px] font-bold tracking-tight">Bangerfy</span>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <a href="#features" className="text-[13px] text-text-muted hover:text-text-primary transition-colors hidden sm:block">Features</a>
            <a href="#pricing" className="text-[13px] text-text-muted hover:text-text-primary transition-colors hidden sm:block">Pricing</a>
            {/* Language switcher */}
            <div className="flex gap-0.5 bg-white/[0.04] rounded-md p-0.5">
              <button
                onClick={() => setLocale('en')}
                className={cn(
                  'px-2 py-0.5 rounded text-[11px] font-medium transition-all border-none cursor-pointer',
                  locale === 'en'
                    ? 'bg-white/[0.1] text-text-primary'
                    : 'bg-transparent text-text-faint hover:text-text-muted'
                )}
              >
                EN
              </button>
              <button
                onClick={() => setLocale('ja')}
                className={cn(
                  'px-2 py-0.5 rounded text-[11px] font-medium transition-all border-none cursor-pointer',
                  locale === 'ja'
                    ? 'bg-white/[0.1] text-text-primary'
                    : 'bg-transparent text-text-faint hover:text-text-muted'
                )}
              >
                JA
              </button>
            </div>
            <button
              onClick={() => window.location.href = '/login'}
              className="px-4 py-1.5 bg-transparent text-text-muted text-[13px] font-medium rounded-lg hover:text-text-primary transition-all border border-white/10 hover:border-white/20"
            >
              Log in
            </button>
            <button
              onClick={handleGetStarted}
              className="px-4 py-1.5 bg-white text-black text-[13px] font-semibold rounded-lg hover:bg-gray-100 transition-all active:scale-[0.97]"
            >
              {t('login.getStarted')}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-20 sm:pt-28 pb-8 px-5">
        {/* Hero background glow */}
        <div className="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-accent/[0.06] rounded-full blur-[160px] pointer-events-none" />
        <div className="absolute top-[60px] right-[15%] w-[250px] h-[250px] bg-brand/[0.04] rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="lp-fade inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/[0.08] bg-white/[0.03] mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            <span className="text-[11px] text-text-muted font-medium">{t('login.badge')}</span>
          </div>

          <h1 className="lp-rise text-[clamp(2.5rem,7vw,4.5rem)] font-extrabold leading-[1.08] tracking-[-0.04em] mb-5">
            {t('login.heroLine1')}
            <br />
            <span className="bg-gradient-to-r from-brand via-blue-400 to-accent bg-clip-text text-transparent">
              {t('login.heroLine2')}
            </span>
          </h1>

          <p className="lp-rise text-text-muted text-lg sm:text-xl max-w-xl mx-auto mb-8 leading-relaxed" style={{ animationDelay: '0.08s' }}>
            {t('login.heroSub')}
          </p>

          <div className="lp-rise flex flex-col sm:flex-row gap-3 justify-center mb-4" style={{ animationDelay: '0.16s' }}>
            <button
              onClick={handleGetStarted}
              className="group px-8 py-3.5 bg-white text-black font-semibold text-[15px] rounded-xl transition-all duration-200 hover:bg-gray-100 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(255,255,255,0.1)] active:scale-[0.98] inline-flex items-center justify-center gap-2"
            >
              {t('login.getStarted')}
              <IconArrowRight className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          <p className="lp-fade text-[12px] text-text-faint" style={{ animationDelay: '0.24s' }}>
            {t('login.ctaHint')}
          </p>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="max-w-6xl mx-auto px-5 py-24">
        <div className="text-center mb-14">
          <p className="text-accent text-[11px] font-semibold tracking-[0.2em] uppercase mb-3">
            {t('login.features.sectionLabel')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.03em] mb-4">
            {t('login.features.sectionTitle')}
          </h2>
          <p className="text-text-muted max-w-lg mx-auto">
            {t('login.features.sectionDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {toolKeys.map((tool) => {
            const Icon = toolIcons[tool.key];
            const isBrand = tool.color === 'brand';
            return (
              <div
                key={tool.key}
                className="group relative p-5 rounded-xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.03] hover:border-white/[0.1] transition-all duration-300"
              >
                <div className={cn(
                  'w-9 h-9 rounded-lg flex items-center justify-center mb-3',
                  isBrand ? 'bg-brand/10' : 'bg-accent/10'
                )}>
                  <Icon className={cn('w-[18px] h-[18px]', isBrand ? 'text-brand' : 'text-accent')} />
                </div>
                <h3 className="text-[14px] font-semibold mb-1.5 group-hover:text-white transition-colors">
                  {t(`login.tools.${tool.key}.label`)}
                </h3>
                <p className="text-[13px] text-text-muted leading-relaxed">
                  {t(`login.tools.${tool.key}.desc`)}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="max-w-4xl mx-auto px-5 py-24">
        <div className="text-center mb-14">
          <p className="text-accent text-[11px] font-semibold tracking-[0.2em] uppercase mb-3">
            {t('login.howItWorks')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.03em]">
            {t('login.howTitle')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            { num: '01', title: t('login.howStep1Title'), desc: t('login.step1') },
            { num: '02', title: t('login.howStep2Title'), desc: t('login.step2') },
            { num: '03', title: t('login.howStep3Title'), desc: t('login.step3') },
          ].map((step, i) => (
            <div key={i} className="relative text-center md:text-left">
              {/* Connector line (desktop) */}
              {i < 2 && (
                <div className="hidden md:block absolute top-5 left-[calc(100%+4px)] w-[calc(100%-28px)] h-px bg-gradient-to-r from-white/[0.08] to-transparent" />
              )}
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent/10 text-accent text-[13px] font-bold font-mono mb-4">
                {step.num}
              </div>
              <h3 className="text-[15px] font-semibold mb-2">{step.title}</h3>
              <p className="text-[13px] text-text-muted leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="max-w-4xl mx-auto px-5 py-24">
        <div className="text-center mb-14">
          <p className="text-accent text-[11px] font-semibold tracking-[0.2em] uppercase mb-3">
            {t('login.pricing.sectionLabel')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.03em] mb-4">
            {t('login.pricing.sectionTitle')}
          </h2>
          <p className="text-text-muted max-w-md mx-auto">
            {t('login.pricing.sectionDesc')}
          </p>
        </div>

        <div className="max-w-sm mx-auto">
          <div className="relative rounded-xl border border-white/[0.06] bg-white/[0.015] p-6">
            <h3 className="text-lg font-bold mb-1">{t('login.pricing.free.name')}</h3>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-3xl font-extrabold tracking-tight">{t('login.pricing.free.price')}</span>
              <span className="text-text-muted text-sm">{t('login.pricing.free.period')}</span>
            </div>
            <p className="text-[13px] text-text-muted mb-5">{t('login.pricing.free.desc')}</p>
            <ul className="space-y-2.5 mb-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-text-secondary">
                  <IconCheck className="text-success shrink-0 mt-0.5" />
                  {t(`login.pricing.free.f${i}`)}
                </li>
              ))}
            </ul>
            <button
              onClick={handleGetStarted}
              className="w-full py-2.5 rounded-lg text-[13px] font-semibold bg-white text-black hover:bg-gray-100 active:scale-[0.98] transition-all"
            >
              {t('login.pricing.free.cta')}
            </button>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="max-w-3xl mx-auto px-5 py-20">
        <div className="relative rounded-2xl border border-white/[0.06] p-10 sm:p-14 text-center overflow-hidden">
          {/* BG glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.06] via-transparent to-brand/[0.04] pointer-events-none" />
          <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-accent/[0.08] rounded-full blur-[80px] pointer-events-none" />

          <h2 className="relative text-2xl sm:text-3xl font-bold tracking-[-0.02em] mb-3">
            {t('login.ctaTitle')}
          </h2>
          <p className="relative text-text-muted mb-8 max-w-md mx-auto text-[15px]">
            {t('login.ctaDesc')}
          </p>
          <button
            onClick={handleGetStarted}
            className="relative group px-10 py-3.5 bg-white text-black font-semibold text-[15px] rounded-xl transition-all duration-200 hover:bg-gray-100 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(255,255,255,0.1)] active:scale-[0.98] inline-flex items-center gap-2"
          >
            {t('login.ctaButton')}
            <IconArrowRight className="group-hover:translate-x-0.5 transition-transform" />
          </button>
          <p className="relative text-[12px] text-text-faint mt-4">
            {t('login.ctaHint')}
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/[0.04] bg-white/[0.01]">
        <div className="max-w-6xl mx-auto px-5 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <img src="/logo192.png" alt="" className="w-6 h-6 rounded-md" />
              <span className="text-[14px] font-bold tracking-tight">Bangerfy</span>
            </div>
            <p className="text-[12px] text-text-faint">
              {t('login.footer')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
